/** @jsx jsx */
import { type ReactNode, memo, useRef, useEffect } from 'react';
import { css, jsx } from '@compiled/react';
import { token } from '@atlaskit/tokens';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { fg } from '@atlassian/jira-feature-gating';
import { useResizeObserver } from '@atlassian/jira-react-use-resize-observer/src/index.tsx';
import ShortcutScope from '@atlassian/jira-common-components-keyboard-shortcuts/src/shortcut-scope.tsx';

export type AnalyticsAttributes = Record<string, string | number | boolean>;

const PopupContentComponent = ({
	children,
	update,
	isInteractive,
	analyticsAttributes,
}: {
	children: ReactNode;
	update: () => void;
	isInteractive?: boolean;
	analyticsAttributes?: AnalyticsAttributes;
}) => {
	const componentRef = useRef(null);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const mountTimeRef = useRef<number>(Date.now());

	useResizeObserver({
		ref: componentRef,
		onResize: update,
	});

	useEffect(() => {
		const mountTime = mountTimeRef.current;

		if (!fg('issue_table_single_line_row_height_fast_follows')) {
			fireUIAnalytics(createAnalyticsEvent({}), 'popup displayed', 'hoverPopover', {
				isInteractive,
				...analyticsAttributes,
			});
		}

		return () => {
			if (fg('issue_table_single_line_row_height_fast_follows')) {
				const duration = Date.now() - mountTime;
				fireUIAnalytics(createAnalyticsEvent({}), 'popup displayed', 'hoverPopover', {
					isInteractive,
					duration,
					...analyticsAttributes,
				});
			}
		};
	}, [createAnalyticsEvent, isInteractive, analyticsAttributes]);

	return (
		<div
			css={popoverStyles}
			ref={componentRef}
			data-testid="hover-popover.ui.popup-content.hover-popover-content"
			aria-hidden={!isInteractive}
			// eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
			tabIndex={isInteractive ? 0 : undefined}
		>
			{children}
			<ShortcutScope />
		</div>
	);
};

export const PopupContent = memo(PopupContentComponent);

const popoverStyles = css({
	backgroundColor: token('elevation.surface.overlay'),
	paddingInline: token('space.150'),
	paddingBlock: token('space.075'),
	borderRadius: token('border.radius'),
	boxShadow: token('elevation.shadow.overlay'),
	fontBody: token('font.body'),
	fontWeight: token('font.weight.regular'),
	overflowWrap: 'break-word',
	maxHeight: '300px',
	wordBreak: 'break-word',
	overflow: 'auto',
	'&:focus': {
		outline: `2px solid ${token('color.border.focused')}`,
		outlineOffset: token('space.025'),
	},
});
